<template>
  <div
    class="survey-modal"
    role="dialog"
    aria-modal="true"
    aria-labelledby="survey-modal-title"
  >
    <!-- ─── Close (optional surveys only — an explicit "Not now") ─── -->
    <button
      v-if="!survey.required"
      class="survey-modal__close"
      :disabled="pending"
      aria-label="Not now"
      @click="emit('snooze')"
    >
      <ion-icon :icon="closeOutline" />
    </button>

    <!-- ─── Header ─── -->
    <div class="survey-modal__head">
      <p class="survey-modal__eyebrow">
        {{ survey.isPlatform ? 'ERPat asks' : 'Your company asks' }}
      </p>
      <span
        v-if="survey.category"
        class="survey-modal__chip"
        :style="chipStyle"
      >{{ survey.category.title }}</span>
      <h2 id="survey-modal-title" class="survey-modal__title">{{ survey.title }}</h2>
    </div>

    <!-- ─── Body + questions (one internal scroller — the scroll gate reads it) ─── -->
    <div ref="scroller" class="survey-modal__scroller" @scroll.passive="checkGate">
      <div ref="scrollContent" class="survey-modal__scroll-content">
        <!-- Admin-authored rich HTML — sanitized once into a computed (FormPage pattern). -->
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-if="survey.bodyHtml" class="survey-modal__body" v-html="safeBody"></div>

        <div v-for="q in visibleQuestions" :key="q.id" class="survey-modal__field">
          <span class="survey-modal__label">
            <!-- Question labels may carry inline HTML — sanitized once into safeLabels. -->
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span v-html="safeLabels[q.id] ?? ''"></span>
            <span v-if="q.required" class="survey-modal__req" aria-hidden="true">*</span>
          </span>

          <!-- rating: 1–5 stars (RateAppModal recipe) -->
          <div v-if="q.inputType === 'rating'" class="survey-modal__stars" role="radiogroup" :aria-label="`Rating for: ${q.label}`">
            <button
              v-for="n in 5"
              :key="n"
              type="button"
              class="survey-modal__star"
              :class="{ 'survey-modal__star--filled': n <= ratingOf(q.id) }"
              role="radio"
              :aria-checked="n === ratingOf(q.id)"
              :aria-label="`${n} star${n > 1 ? 's' : ''}`"
              :disabled="pending"
              @click="scalar[q.id] = String(n)"
            >
              <ion-icon :icon="n <= ratingOf(q.id) ? star : starOutline" />
            </button>
          </div>

          <!-- scale: numeric / labelled segment -->
          <div v-else-if="q.inputType === 'scale'" class="survey-modal__segments" role="radiogroup" :aria-label="`Scale for: ${q.label}`">
            <button
              v-for="opt in scaleOptions(q)"
              :key="opt"
              type="button"
              class="survey-modal__segment"
              :class="{ 'survey-modal__segment--on': scalar[q.id] === opt }"
              role="radio"
              :aria-checked="scalar[q.id] === opt"
              :disabled="pending"
              @click="scalar[q.id] = opt"
            >{{ opt }}</button>
          </div>

          <!-- single_choice: radio pills -->
          <div v-else-if="q.inputType === 'single_choice'" class="survey-modal__choices" role="radiogroup" :aria-label="q.label">
            <label
              v-for="opt in q.options"
              :key="opt"
              class="survey-modal__choice"
              :class="{ 'survey-modal__choice--on': scalar[q.id] === opt }"
            >
              <input
                type="radio"
                class="survey-modal__choice-box"
                :name="`sq-${q.id}`"
                :value="opt"
                :checked="scalar[q.id] === opt"
                :disabled="pending"
                @change="scalar[q.id] = opt"
              />
              <span>{{ opt }}</span>
            </label>
          </div>

          <!-- multi_choice: checkbox pills (FormPage multi_select recipe) -->
          <div v-else-if="q.inputType === 'multi_choice'" class="survey-modal__choices" role="group" :aria-label="q.label">
            <label
              v-for="opt in q.options"
              :key="opt"
              class="survey-modal__choice"
              :class="{ 'survey-modal__choice--on': (multi[q.id] ?? []).includes(opt) }"
            >
              <input
                type="checkbox"
                class="survey-modal__choice-box"
                :value="opt"
                :checked="(multi[q.id] ?? []).includes(opt)"
                :disabled="pending"
                @change="toggleMulti(q.id, opt)"
              />
              <span>{{ opt }}</span>
            </label>
          </div>

          <!-- agreement (non-consent surveys): an Agree / Disagree pill pair -->
          <div v-else-if="q.inputType === 'agreement'" class="survey-modal__choices" role="radiogroup" :aria-label="q.label">
            <label
              v-for="opt in AGREEMENT_OPTIONS"
              :key="opt.value"
              class="survey-modal__choice"
              :class="{ 'survey-modal__choice--on': scalar[q.id] === opt.value }"
            >
              <input
                type="radio"
                class="survey-modal__choice-box"
                :name="`sq-${q.id}`"
                :value="opt.value"
                :checked="scalar[q.id] === opt.value"
                :disabled="pending"
                @change="scalar[q.id] = opt.value"
              />
              <span>{{ opt.label }}</span>
            </label>
          </div>

          <!-- text (and any unknown type, which the mapper degrades to text) -->
          <textarea
            v-else
            v-model="scalar[q.id]"
            class="survey-modal__textarea"
            :class="{ 'survey-modal__input--error': !!errorFor(q.id) }"
            rows="3"
            placeholder="Type your answer…"
            :disabled="pending"
            :aria-invalid="!!errorFor(q.id)"
            :aria-label="q.label"
          />

          <p v-if="errorFor(q.id)" class="survey-modal__field-error" role="alert">{{ errorFor(q.id) }}</p>
        </div>
      </div>
    </div>

    <!-- ─── Inline submit error (the modal stays open — retry is available) ─── -->
    <p v-if="error" class="survey-modal__error" role="alert">{{ error }}</p>
    <p v-else-if="gateBlocked" class="survey-modal__hint">Scroll to the end to agree.</p>

    <!-- ─── Actions ─── -->
    <div class="survey-modal__actions">
      <template v-if="isConsent">
        <button class="survey-modal__primary" :disabled="pending || gateBlocked" @click="onAgree">
          <ion-spinner v-if="pending" name="crescent" class="survey-modal__spinner" />
          <template v-else>I Agree</template>
        </button>
        <button class="survey-modal__secondary" :disabled="pending" @click="emit('decline')">
          I Disagree
        </button>
      </template>
      <template v-else>
        <button class="survey-modal__primary" :disabled="pending" @click="onSubmit">
          <ion-spinner v-if="pending" name="crescent" class="survey-modal__spinner" />
          <template v-else>Submit</template>
        </button>
      </template>

      <template v-if="!survey.required">
        <button class="survey-modal__secondary" :disabled="pending" @click="emit('snooze')">
          Not now{{ survey.snoozeDays > 0 ? ` (ask again in ${survey.snoozeDays}d)` : '' }}
        </button>
        <button class="survey-modal__ghost" :disabled="pending" @click="emit('optOut')">
          Don't ask again
        </button>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, reactive, ref } from 'vue';
import { IonIcon, IonSpinner } from '@ionic/vue';
import { closeOutline, star, starOutline } from 'ionicons/icons';
import { sanitizeHtml } from '@/utils/sanitize-html';
import type { SurveyPromptVM, SurveyQuestionVM } from '@/services/api/models';
import type { SurveyAnswerMap } from '@/services/erpat';

/**
 * The survey/consent popup content. STATELESS per the strict modal pattern:
 * the host (`SurveyPromptHost`) owns all async state and keys this component by
 * the survey token, so advancing to the next survey remounts it with fresh
 * answer state. Emits intents only — never talks to the store or the network.
 *
 * Required surveys render with NO escape (no ✕, no snooze/opt-out — and the
 * host sets `backdrop-dismiss` false); a consent survey is satisfied by EITHER
 * Agree or Disagree, so the user is never trapped.
 */
const props = withDefaults(
  defineProps<{
    survey: SurveyPromptVM;
    /** True while the host is submitting — locks controls, spinner on the primary. */
    pending?: boolean;
    /** Submit failure to show inline (the modal stays open). */
    error?: string | null;
    /** Server 422 per-question errors keyed by question id. */
    fieldErrors?: Record<string, string>;
  }>(),
  { pending: false, error: null, fieldErrors: () => ({}) },
);

const emit = defineEmits<{
  (e: 'answer', answers: SurveyAnswerMap): void;
  (e: 'agree', answers?: SurveyAnswerMap): void;
  (e: 'decline'): void;
  (e: 'snooze'): void;
  (e: 'optOut'): void;
}>();

const AGREEMENT_OPTIONS = [
  { value: 'agree', label: 'Agree' },
  { value: 'disagree', label: 'Disagree' },
] as const;

const isConsent = computed(() => props.survey.surveyType === 'consent');

/**
 * Sanitize ONCE per survey (the host keys this component by token, so props
 * never change mid-mount). Inline sanitizeHtml() calls in the template would
 * re-run a full DOMParser parse on every keystroke/star tap — the codebase
 * precedent is a computed (NoteDetailModal's safeBody).
 */
const safeBody = computed(() => sanitizeHtml(props.survey.bodyHtml));
const safeLabels = computed<Record<string, string>>(() => {
  const map: Record<string, string> = {};
  for (const q of props.survey.questions) map[q.id] = sanitizeHtml(q.label);
  return map;
});

/** Tint the category chip with the admin-chosen hex; anything else keeps the default teal tint. */
const chipStyle = computed<Record<string, string>>(() => {
  const styles: Record<string, string> = {};
  const color = props.survey.category?.color ?? '';
  if (/^#[0-9a-fA-F]{6}$/.test(color)) {
    styles.background = `${color}1f`;
    styles.color = color;
  }
  return styles;
});

/**
 * On a CONSENT survey the footer's I Agree / I Disagree pair IS the agreement,
 * so agreement-type questions are not rendered a second time. Every other type
 * renders all questions.
 */
const visibleQuestions = computed<SurveyQuestionVM[]>(() =>
  isConsent.value
    ? props.survey.questions.filter((q) => q.inputType !== 'agreement')
    : props.survey.questions,
);

// ── Answer state (fresh per mount — the host keys this component by token) ──
const scalar = reactive<Record<string, string>>({});
const multi = reactive<Record<string, string[]>>({});
const localErrors = reactive<Record<string, string>>({});

const ratingOf = (id: string): number => Number.parseInt(scalar[id] ?? '0', 10) || 0;

function scaleOptions(q: SurveyQuestionVM): string[] {
  return q.options.length ? q.options : ['1', '2', '3', '4', '5'];
}

function toggleMulti(id: string, opt: string): void {
  const cur = multi[id] ?? [];
  multi[id] = cur.includes(opt) ? cur.filter((o) => o !== opt) : [...cur, opt];
}

function errorFor(id: string): string {
  return localErrors[id] ?? props.fieldErrors[id] ?? '';
}

// ── Scroll gate (consent): Agree unlocks once the content has been read ─────
const scroller = ref<HTMLElement | null>(null);
const scrollContent = ref<HTMLElement | null>(null);
const gateSatisfied = ref(false);
let resizeObserver: ResizeObserver | null = null;

const gateBlocked = computed(
  () => isConsent.value && props.survey.scrollGate && !gateSatisfied.value,
);

function checkGate(): void {
  if (gateSatisfied.value) return;
  const el = scroller.value;
  if (!el) return;
  // Not laid out yet: ion-modal keeps its host display:none ('overlay-hidden')
  // until present() runs, while the inline slot content has already mounted —
  // every dimension reads 0 and '0 + 0 >= 0 - 8' would satisfy the gate
  // vacuously. Only a REAL layout may satisfy it; the ResizeObserver re-checks
  // the moment the modal becomes visible.
  if (el.clientHeight === 0 && el.scrollHeight === 0) return;
  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) gateSatisfied.value = true;
}

onMounted(() => {
  // Content that fits without scrolling satisfies the gate immediately; a
  // ResizeObserver re-checks when late-loading content changes the height.
  void nextTick(() => checkGate());
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => checkGate());
    if (scroller.value) resizeObserver.observe(scroller.value);
    if (scrollContent.value) resizeObserver.observe(scrollContent.value);
  }
});
onUnmounted(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
});

// ── Validate + collect (FormPage's validate()/buildAnswers() shape) ─────────
function validate(): boolean {
  for (const key of Object.keys(localErrors)) delete localErrors[key];
  let ok = true;
  for (const q of visibleQuestions.value) {
    if (!q.required) continue;
    const filled =
      q.inputType === 'multi_choice'
        ? (multi[q.id] ?? []).length > 0
        : (scalar[q.id] ?? '').trim() !== '';
    if (!filled) {
      localErrors[q.id] = 'This question is required.';
      ok = false;
    }
  }
  return ok;
}

function buildAnswers(): SurveyAnswerMap {
  const answers: SurveyAnswerMap = {};
  for (const q of visibleQuestions.value) {
    if (q.inputType === 'multi_choice') {
      const values = multi[q.id] ?? [];
      if (values.length) answers[q.id] = values;
    } else {
      const value = (scalar[q.id] ?? '').trim();
      if (value !== '') answers[q.id] = value;
    }
  }
  return answers;
}

function onSubmit(): void {
  if (props.pending) return;
  if (!validate()) return;
  emit('answer', buildAnswers());
}

function onAgree(): void {
  if (props.pending || gateBlocked.value) return;
  // Agreeing also submits any extra (non-agreement) questions the consent
  // survey carries — validate the required ones first. Disagree never gates.
  if (!validate()) return;
  const answers = buildAnswers();
  emit('agree', Object.keys(answers).length ? answers : undefined);
}
</script>

<!-- Non-scoped: the Ionic modal renders in a portal outside this component, so
     the centering / glass host styles must be global. The host opts in with
     css-class="survey-prompt-modal" (the ConfirmModal recipe). -->
<style lang="scss">
ion-modal.survey-prompt-modal {
  --background: transparent;
  --width: 90%;
  --max-width: 420px;
  --height: auto;
  --max-height: 88vh;
  --border-radius: var(--syntry-radius-modal);
  --box-shadow: none;
  --backdrop-opacity: 0.32;
  align-items: center;
  justify-content: center;
}

ion-modal.survey-prompt-modal .ion-page {
  position: relative;
  contain: content;
  background: transparent;
  height: auto;
}
</style>

<style scoped lang="scss">
.survey-modal {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
  max-height: 88vh;
  padding: var(--sp-7) var(--sp-6) var(--sp-6);
  /* Frosted-glass surface — matches ConfirmModal / RateAppModal. */
  background: rgba(var(--syntry-surface-rgb), 0.82);
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  border: 1px solid rgba(var(--syntry-surface-rgb), 0.55);
  border-radius: var(--syntry-radius-modal);
  box-shadow: var(--syntry-shadow-elevated);

  &__close {
    position: absolute;
    top: var(--sp-4);
    right: var(--sp-4);
    width: 36px;
    height: 36px;
    min-width: 36px;
    background: rgba(var(--syntry-surface-rgb), 0.6);
    border: none;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 20px;
    color: var(--syntry-text-muted);
    padding: 0;
    -webkit-tap-highlight-color: transparent;

    &:active { opacity: 0.7; }
    &:disabled { opacity: 0.4; cursor: not-allowed; }
  }

  &__head {
    display: flex;
    flex-direction: column;
    gap: var(--sp-2);
    padding-right: var(--sp-8); // clear the ✕
  }

  &__eyebrow {
    margin: 0;
    font-size: var(--syntry-fs-caption);
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--syntry-primary);
  }

  &__chip {
    align-self: flex-start;
    padding: 2px 10px;
    border-radius: 999px;
    font-size: var(--syntry-fs-caption);
    font-weight: 600;
    color: var(--syntry-text-strong);
    background: rgba(var(--ion-color-primary-rgb), 0.12);
  }

  &__title {
    margin: 0;
    font-size: 19px;
    font-weight: 800;
    letter-spacing: -0.01em;
    color: var(--syntry-text-strong);
  }

  &__scroller {
    overflow-y: auto;
    min-height: 0;
    flex: 1 1 auto;
    border-radius: var(--syntry-radius-md);
  }

  &__scroll-content {
    display: flex;
    flex-direction: column;
    gap: var(--sp-4);
  }

  &__body {
    font-size: var(--syntry-fs-body);
    color: var(--syntry-text);
    line-height: 1.6;

    :deep(p) { margin: 0 0 var(--sp-3); }
    :deep(p:last-child) { margin-bottom: 0; }
    :deep(a) { color: var(--syntry-primary); }
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: var(--sp-2);
  }

  &__label {
    font-size: var(--syntry-fs-small);
    font-weight: 600;
    color: var(--syntry-text-strong);
  }

  &__req { color: var(--syntry-red, #d64545); margin-left: 2px; }

  &__stars {
    display: flex;
    align-items: center;
    gap: var(--sp-2);
  }

  &__star {
    background: none;
    border: none;
    padding: var(--sp-1);
    cursor: pointer;
    font-size: 32px;
    color: var(--syntry-border);
    display: flex;
    align-items: center;
    justify-content: center;
    -webkit-tap-highlight-color: transparent;
    transition: transform 120ms ease, color 120ms ease;

    &--filled { color: var(--syntry-orange); }
    &:active { transform: scale(0.9); }
    &:disabled { cursor: not-allowed; }
  }

  &__segments {
    display: flex;
    flex-wrap: wrap;
    gap: var(--sp-2);
  }

  &__segment {
    min-width: 44px;
    padding: var(--sp-2) var(--sp-3);
    border: 1px solid var(--syntry-border);
    border-radius: var(--syntry-radius-md);
    background: rgba(var(--syntry-surface-rgb), 0.5);
    font-size: var(--syntry-fs-body);
    font-weight: 600;
    font-family: var(--syntry-font);
    color: var(--syntry-text);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &--on {
      border-color: var(--syntry-primary);
      background: rgba(var(--ion-color-primary-rgb), 0.12);
      color: var(--syntry-primary);
    }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
  }

  &__choices {
    display: flex;
    flex-direction: column;
    gap: var(--sp-2);
  }

  &__choice {
    display: flex;
    align-items: center;
    gap: var(--sp-3);
    padding: var(--sp-3) var(--sp-4);
    border: 1px solid var(--syntry-border);
    border-radius: var(--syntry-radius-md);
    background: rgba(var(--syntry-surface-rgb), 0.5);
    font-size: var(--syntry-fs-body);
    color: var(--syntry-text);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &--on {
      border-color: var(--syntry-primary);
      background: rgba(var(--ion-color-primary-rgb), 0.1);
    }
  }

  &__choice-box { accent-color: var(--syntry-primary); }

  &__textarea {
    width: 100%;
    border: 1px solid var(--syntry-border);
    border-radius: var(--syntry-radius-md);
    padding: var(--sp-3) var(--sp-4);
    font-size: var(--syntry-fs-body);
    font-family: var(--syntry-font);
    color: var(--syntry-text);
    background: rgba(var(--syntry-surface-rgb), 0.5);
    outline: none;
    resize: none;
    box-sizing: border-box;

    &::placeholder { color: var(--syntry-text-soft); }
    &:focus {
      border-color: var(--syntry-primary);
      box-shadow: 0 0 0 3px rgba(var(--ion-color-primary-rgb), 0.15);
    }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
  }

  &__input--error { border-color: var(--syntry-red, #d64545); }

  &__field-error,
  &__error {
    margin: 0;
    font-size: var(--syntry-fs-small);
    font-weight: 600;
    color: var(--syntry-red, #d64545);
  }

  &__hint {
    margin: 0;
    font-size: var(--syntry-fs-small);
    color: var(--syntry-text-muted);
    text-align: center;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: var(--sp-2);
  }

  &__primary {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--sp-2);
    height: 50px;
    border: none;
    border-radius: var(--syntry-radius-button);
    background: var(--syntry-gradient-primary);
    color: #ffffff;
    font-size: 16px;
    font-weight: 700;
    font-family: var(--syntry-font);
    cursor: pointer;
    box-shadow: var(--syntry-shadow-button);
    transition: opacity 150ms ease;

    &:disabled { opacity: 0.45; cursor: not-allowed; box-shadow: none; }
    &:not(:disabled):active { opacity: 0.88; }
  }

  &__secondary {
    height: 46px;
    border: 1px solid var(--syntry-border);
    border-radius: var(--syntry-radius-button);
    background: transparent;
    color: var(--syntry-text-strong);
    font-size: var(--syntry-fs-body);
    font-weight: 600;
    font-family: var(--syntry-font);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:disabled { opacity: 0.5; cursor: not-allowed; }
    &:not(:disabled):active { opacity: 0.8; }
  }

  &__ghost {
    border: none;
    background: none;
    padding: var(--sp-2);
    font-size: var(--syntry-fs-small);
    font-weight: 600;
    font-family: var(--syntry-font);
    color: var(--syntry-text-muted);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:disabled { opacity: 0.5; cursor: not-allowed; }
  }

  &__spinner {
    width: 22px;
    height: 22px;
    --color: #ffffff;
  }
}
</style>
